import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import LinkIconLight from 'assets/svg/app/link-light.svg'
import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ProgressSteps from 'components/ProgressSteps'
import SplitSelect from 'components/SplitSelect'
import { Body } from 'components/Text'

import SelectTradingType from 'sections/shared/components/SelectTradingType'
import { setOpenModal } from 'state/app/reducer'
import { delegateToOneClickTrading, startOneClickSession } from 'state/futures/actions'
import { selectTradingMode } from 'state/futures/common/selectors'
import { selectSubmittingFuturesTx, selectTradePreview } from 'state/futures/selectors'
import { TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { type DateInterval, INTERVAL_OPTIONS, convertSecondsToExpiryDate } from 'utils/dates'

import useAbstractionInfo from '../Trade/OneClickTrading/hooks/useAbstractionInfo'

type Props = {
	isOpen: boolean
}

const FORMATTED_INTERVAL_OPTIONS = INTERVAL_OPTIONS.map((s) => ({
	type: 'button' as const,
	value: s,
}))

export default function OneClickTradingOnboard({ isOpen }: Props) {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const txProcessing = useAppSelector(selectSubmittingFuturesTx)
	const preview = useAppSelector(selectTradePreview)
	const tradingMode = useAppSelector(selectTradingMode)

	const { openWallet, truncatedAddress, isDelegated } = useAbstractionInfo()

	const onClose = useCallback(() => dispatch(setOpenModal(null)), [dispatch])

	const onComplete = useCallback(() => {
		if (preview) {
			dispatch(setOpenModal('futures_confirm_isolated_margin_trade'))
		} else {
			onClose()
		}
	}, [dispatch, onClose, preview])

	const delegateAccount = useCallback(() => {
		dispatch(delegateToOneClickTrading())
	}, [dispatch])

	const [interval, setInterval] = useState<DateInterval>(INTERVAL_OPTIONS[3])

	const activateSession = useCallback(async () => {
		await dispatch(startOneClickSession(interval))
		onComplete()
	}, [dispatch, interval, onComplete])

	const renderContent = useMemo(() => {
		const renderProgress = (step: number, complete?: boolean) => {
			return (
				<ProgressContainer>
					<ProgressSteps step={step} totalSteps={2} complete={complete} />
				</ProgressContainer>
			)
		}

		if (isDelegated) {
			return (
				<>
					<Intro>{t('futures.one-click-trading.onboard.duration-select')}</Intro>
					<span style={{ marginBottom: '10px' }}>
						{t('futures.one-click-trading.onboard.duration')}:
					</span>
					<SplitSelect<DateInterval>
						selected={interval}
						options={FORMATTED_INTERVAL_OPTIONS}
						onSelect={(interval) => setInterval(interval!)}
					/>
					<InfoContainer>
						<InfoBlock>
							<InfoBlockTitle>{t('futures.one-click-trading.expires-in')}</InfoBlockTitle>
							<InfoBlockValue>{convertSecondsToExpiryDate(interval)}</InfoBlockValue>
						</InfoBlock>
						<InfoBlock>
							<InfoBlockTitle>{t('common.address')}</InfoBlockTitle>
							<InfoBlockValue>
								{truncatedAddress}
								<LinkIconLight
									onClick={openWallet}
									height={18}
									width={18}
									style={{ paddingLeft: 5, cursor: 'pointer' }}
								/>
							</InfoBlockValue>
						</InfoBlock>
					</InfoContainer>
					{renderProgress(2)}
					<StyledButton
						variant="solid-highlight"
						loading={txProcessing}
						noOutline
						onClick={activateSession}
						disabled={txProcessing}
					>
						{t('futures.one-click-trading.onboard.start-session')}
					</StyledButton>
				</>
			)
		}

		return (
			<>
				<Intro>{t('futures.one-click-trading.onboard.select-mode')}</Intro>
				<div>
					<SelectTradingType />
				</div>
				{renderProgress(1)}
				<StyledButton
					noOutline
					onClick={tradingMode === TradingModes.DEFAULT ? onComplete : delegateAccount}
					disabled={txProcessing}
					loading={txProcessing}
				>
					{tradingMode === TradingModes.DEFAULT
						? t('futures.one-click-trading.onboard.back')
						: t('futures.one-click-trading.onboard.activate')}
				</StyledButton>
			</>
		)
	}, [
		activateSession,
		delegateAccount,
		interval,
		isDelegated,
		onComplete,
		openWallet,
		t,
		tradingMode,
		truncatedAddress,
		txProcessing,
	])

	return (
		<StyledBaseModal
			isWide
			onDismiss={onClose}
			isOpen={isOpen}
			title={t('futures.one-click-trading.title')}
		>
			{renderContent}
		</StyledBaseModal>
	)
}

const StyledBaseModal = styled(BaseModal)<{ isWide: boolean }>`
	z-index: 100;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	[data-reach-dialog-content] {
		width: ${(props) => (props.isWide ? '480px' : '400px')};
	}
`

const StyledButton = styled(TransactionButton)`
	margin-top: 24px;
	height: 50px;
	width: 100%;
`

const ProgressContainer = styled.div`
	margin-top: 30px;
`

const Intro = styled.div`
	margin-bottom: 30px;
`

const InfoBlock = styled.div`
	width: calc(50% - 15px);
	display: flex;
	flex-direction: column;
	gap: 5px;
`

const InfoBlockTitle = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.text.label};
	text-transform: capitalize;
`

const InfoBlockValue = styled(Body).attrs({ mono: true })`
	color: ${(props) => props.theme.colors.selectedTheme.text.value};
`

const InfoContainer = styled.div`
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 15px;
	align-items: center;
	padding: 15px;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 4px;
`
